import { Activity, ExternalLink, Radio } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import RunStatusBadge from "@/features/workflows/components/RunStatusBadge";
import type { WorkflowCanvasRunOverlay } from "./workflow-canvas-overlay.types";

interface WorkflowLiveRunStripProps {
  workflowId: string;
  overlay: WorkflowCanvasRunOverlay;
}

export function WorkflowLiveRunStrip({ workflowId, overlay }: WorkflowLiveRunStripProps) {
  const t = useTranslations("workflows");
  const navigate = useNavigate();
  if (!overlay.runId) return null;

  const openRunDetail = () => {
    void navigate({
      to: "/workflows/$id/runs/$runId",
      params: { id: workflowId, runId: overlay.runId ?? "" },
    });
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 dark:border-emerald-900/60 dark:bg-emerald-950/30">
      <div className="flex min-w-0 items-center gap-3">
        <span className="relative flex size-8 shrink-0 items-center justify-center rounded-md bg-white text-emerald-700 shadow-sm dark:bg-gray-900 dark:text-emerald-300">
          <Activity className="size-4" />
          {overlay.isConnected && (
            <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-50 dark:ring-emerald-950" />
          )}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {t("live_canvas.title")}
            </span>
            {overlay.runStatus && <RunStatusBadge status={overlay.runStatus} />}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <Radio className="size-3" />
            <span>{overlay.useFallbackPolling ? t("live_canvas.polling") : t("live_canvas.streaming")}</span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={openRunDetail}
        className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-gray-950 dark:text-emerald-200 dark:hover:bg-emerald-950"
      >
        <ExternalLink className="size-3.5" />
        {t("live_canvas.open_run")}
      </button>
    </div>
  );
}
